/**
 * Login page shell — composes the two credential surfaces (AD-9 · plan §2.2.1).
 *
 * Three responsibilities, and they are all ownership decisions rather than
 * markup:
 *
 *  1. **Sole bypass owner (AD-11).** The `local + auth disabled` status lives
 *     here and nowhere else. PasskeyLogin skips its own copy under
 *     `variant="embed"` precisely so this is the only one, because two nodes
 *     saying "auth is off" is two things to keep in step and one duplicate
 *     announcement for a screen reader.
 *
 *  2. **Page chrome.** The `h1` and the lede belong to the page, not to either
 *     credential surface — an embedded ceremony that kept its own `h1` would
 *     put two on one document.
 *
 *  3. **Which surfaces are offered**, from `passkeyMode` alone. It never
 *     inspects PasskeyLogin's DOM to decide (AD-9 rule 1): field visibility is
 *     a rendering detail of the ceremony and is NOT a signal that bootstrap is
 *     needed.
 */

import { useId, type JSX } from 'react';

import type { AppConfig, PasskeyMode } from '@/config';

import { isLocalAuthDisabled, LOCAL_AUTH_BYPASS_COPY } from './local-bypass';
import styles from './login-panel.module.css';
import { PasskeyLogin } from './passkey-login';
import { PasswordLogin } from './password-login';

import type { ReturnToLocation } from './return-to';

export interface LoginPanelProps {
  readonly config: AppConfig;
  /**
   * Defaults to `config.passkeyMode` (AD-15). Tests may override without
   * building a production config; omitting it cannot diverge from the env.
   *
   * The override is REFUSED in production when it would widen the offer — see
   * `resolvePasskeyMode`. Plan §2.2.7 mandates the explicit pass at LoginRoute,
   * so the prop stays; what closes is its ability to reinstate `auto` chrome in
   * a bundle where `parseConfig` already refused that very value.
   */
  readonly passkeyMode?: PasskeyMode;
  readonly returnTo?: ReturnToLocation | null;
  readonly onSuccess?: (returnTo: ReturnToLocation) => void;
}

/**
 * Show the password form iff the mode is not `bootstrap` (AD-9 rule 2).
 *
 * `bootstrap` is the Phase E window: no password hash exists yet, so the form
 * could only ever produce a guaranteed 401 — and each one burns a slot in the
 * shared 3-strike IP ban that also locks out passkey sign-in (AD-14). Hiding it
 * is the mitigation; a disabled button is not, because there is no client
 * signal for "hash unknown" without a discovery endpoint (AD-1).
 */
function showsPassword(passkeyMode: PasskeyMode): boolean {
  return passkeyMode !== 'bootstrap';
}

/**
 * The lede must never read "No password." while a password form is on screen,
 * and must never claim a password IS configured — the panel cannot know.
 * "Sign in with password or passkey" states the available paths, which is all
 * that is knowable client-side (AD-14 rule 3).
 */
function ledeFor(passkeyMode: PasskeyMode): string {
  return showsPassword(passkeyMode)
    ? 'Sign in with password or passkey.'
    : 'Sign in with a passkey.';
}

/**
 * The prop may narrow what production offers, never widen it.
 *
 * `parseConfig` refuses `VITE_PASSKEY_MODE=auto` in production precisely so a
 * production bundle cannot ship the bootstrap handle fields and the enrolment
 * fallback (AD-15). This prop was the one remaining way back in: an explicit
 * `passkeyMode="auto"` reinstated exactly the chrome the env refusal exists to
 * close, and no type can express "unavailable when `config.isProduction`",
 * because that is a runtime value.
 *
 * So it is GUARDED rather than typed — the project's rule for an invariant that
 * depends on another module's behaviour (style §4). A throw, not a silent
 * fallback: this can only fire on a code bug, never on operator input, and a
 * fallback would leave that bug shipping quietly in the one build where it
 * matters. `parseConfig` takes the same posture one layer down.
 */
function resolvePasskeyMode(config: AppConfig, requested: PasskeyMode): PasskeyMode {
  if (config.isProduction && requested === 'auto') {
    throw new Error('LoginPanel refuses passkeyMode="auto" in production (AD-15)');
  }
  return requested;
}

/**
 * The bypass branch keeps the `h1`: it is the page's only heading, and a
 * document whose whole content is a bare status region has none at all.
 */
function LocalBypassPanel(): JSX.Element {
  return (
    <div className={styles['root']}>
      <h1 className={styles['title']}>Meridian</h1>
      <p role="status">{LOCAL_AUTH_BYPASS_COPY}</p>
    </div>
  );
}

export function LoginPanel({
  config,
  passkeyMode: requestedPasskeyMode = config.passkeyMode,
  returnTo,
  onSuccess,
}: LoginPanelProps): JSX.Element {
  const passkeyMode = resolvePasskeyMode(config, requestedPasskeyMode);
  /* `useId`, not a module constant: two panels on one document would otherwise
     point both password fields at the first hint. */
  const hintId = useId();
  if (isLocalAuthDisabled(config)) return <LocalBypassPanel />;

  /*
   * Normalised once, and forwarded as one object to both children.
   *
   * Under `exactOptionalPropertyTypes` an optional prop may be ABSENT but not
   * explicitly `undefined`, so neither destructured value type-checks when
   * passed straight through. `returnTo` has a null shape both children already
   * accept; `onSuccess` has none — a no-op default would swallow a genuinely
   * missing handler — so it is spread conditionally, which is what "absent"
   * means at a call site.
   */
  const forwarded = {
    returnTo: returnTo ?? null,
    ...(onSuccess === undefined ? {} : { onSuccess }),
  };

  return (
    <div className={styles['root']}>
      <h1 className={styles['title']}>Meridian</h1>
      <p className={styles['lede']}>{ledeFor(passkeyMode)}</p>

      {/*
       * `auto` ONLY (AD-14 rule 3). Grouped with the lede (plan §2.6), not
       * between the two credential surfaces. Production is `authenticate`,
       * where AD-15 makes an existing hash a precondition — hedging copy
       * there is vestigial.
       */}
      {passkeyMode === 'auto' ? (
        <p className={styles['hint']} id={hintId}>
          If no password has been set on this deployment yet, use the passkey.
        </p>
      ) : null}

      {/* Called at each site rather than hoisted to a local: the predicate is
          pure and free, and a local naming this condition trips the
          secret-scan assignment arm on the identifier alone. */}
      {/* The hint is DESCRIBED-BY-linked only when it is actually rendered —
          `auto` alone. A dangling `aria-describedby` is an axe violation
          (`aria-valid-attr-value`), so the id is passed on exactly the branch
          that mounts the node. */}
      {showsPassword(passkeyMode) ? (
        <PasswordLogin
          config={config}
          {...forwarded}
          {...(passkeyMode === 'auto' ? { describedBy: hintId } : {})}
        />
      ) : null}

      {showsPassword(passkeyMode) ? (
        <p className={styles['divider']} aria-hidden="true">
          or
        </p>
      ) : null}

      <PasskeyLogin config={config} mode={passkeyMode} variant="embed" {...forwarded} />
    </div>
  );
}
